import { useState } from "react";
import { FileText } from "lucide-react";
import { applicableAnswers, questionError, questionVisible } from "./answers";
import type { IntakeAnswer, IntakeAnswers, IntakeQuestion } from "./types";
import { MeasurementInput } from "./MeasurementInput";
import { HEIGHT_KEY, intakeBmi, WEIGHT_KEY } from "./measurements";
import { useUploadDocument } from "@/lib/api/hooks";
import { IntakeFormField } from "../IntakeFormField";

const SEX_KEY = "demographics.sex_assigned_at_birth";
const sexOptions = ["Male", "Female", "Unsure"];

function fieldValue(
  question: IntakeQuestion,
  value: IntakeAnswer | undefined,
): string {
  if (question.key === SEX_KEY && typeof value === "string")
    return (
      sexOptions.find(
        (option) => option.toLowerCase() === value.trim().toLowerCase(),
      ) ?? value
    );
  if (question.type === "acknowledgement") return value === true ? "Yes" : "";
  if (question.type === "boolean")
    return typeof value === "boolean" ? (value ? "yes" : "no") : "";
  return Array.isArray(value)
    ? value.join("\n")
    : value == null
      ? ""
      : String(value);
}

function answerValue(question: IntakeQuestion, value: string): IntakeAnswer {
  if (question.type === "acknowledgement") return value === "Yes";
  if (question.type === "boolean") return value === "yes";
  if (question.type === "multi_select") return value ? value.split("\n") : [];
  if (question.type === "number") return value === "" ? null : Number(value);
  return value;
}

/** Canonical values use the same patient controls as the existing product intake. */
export function IntakeFields({
  questions,
  answers,
  onChange,
  attempted,
  masterAnswers = answers,
  disabled = false,
  serverErrors = {},
  onUploadStart,
  onUploadEnd,
}: {
  questions: IntakeQuestion[];
  answers: IntakeAnswers;
  onChange: (id: string, value: IntakeAnswer) => void;
  attempted: boolean;
  masterAnswers?: IntakeAnswers;
  disabled?: boolean;
  serverErrors?: Record<string, string>;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
}) {
  const visibleAnswers = applicableAnswers(questions, answers, masterAnswers);
  // Include unanswered visible fields while excluding stale hidden descendants.
  const visibleQuestions = questions.filter((question) =>
    questionVisible(question, visibleAnswers, masterAnswers),
  );
  const bmi = intakeBmi(visibleAnswers, questions);
  return (
    <div className="flex flex-col">
      {visibleQuestions.map((question, index) => {
        const value = answers[question.key];
        const validation = attempted
          ? questionError(question, visibleAnswers, masterAnswers)
          : null;
        const error =
          serverErrors[question.key] ||
          (validation === "Please answer this question."
            ? "THIS QUESTION IS REQUIRED"
            : validation);
        const types: Record<string, string> = {
          single_select: "radio",
          multi_select: "checkbox",
          boolean: "radio",
          acknowledgement: "agree_to_above",
        };
        const choices =
          question.type === "boolean"
            ? [
                { value: "yes", label: "Yes" },
                { value: "no", label: "No" },
              ]
            : question.options;
        const isSex = question.key === SEX_KEY;
        const currentSex = fieldValue(question, value);
        const displayedSexOptions = [
          ...sexOptions,
          ...(currentSex && !sexOptions.includes(currentSex)
            ? [currentSex]
            : []),
        ].map((option) => ({ value: option, label: option }));
        return (
          <div
            key={question.key}
            className={
              index === 0 ? "" : "mt-25 pt-25 border-t border-line-subtle"
            }
          >
            <IntakeFormField
              fieldId={`intake-question-${question.key}`}
              module={{
                id: question.key,
                label: question.label,
                mod_type: isSex
                  ? "sex"
                  : (types[question.type] ?? question.type),
                required: question.required,
                options: "",
              }}
              value={fieldValue(question, value)}
              onChange={(next) =>
                onChange(question.key, answerValue(question, next))
              }
              choices={isSex ? displayedSexOptions : choices}
              exclusiveOptions={question.exclusive_options}
              error={error}
              helpText={question.help_text}
              disabled={disabled}
              min={question.min}
              max={question.max}
            >
              {question.type === "file" ? (
                <IntakeFile
                  question={question}
                  value={value}
                  onUploaded={(reference) => onChange(question.key, reference)}
                  onUploadStart={onUploadStart}
                  onUploadEnd={onUploadEnd}
                />
              ) : question.type === "number" &&
                [HEIGHT_KEY, WEIGHT_KEY].includes(question.key) ? (
                <MeasurementInput
                  question={question}
                  value={value}
                  error={Boolean(error)}
                  onChange={(next) => onChange(question.key, next)}
                />
              ) : undefined}
            </IntakeFormField>
          </div>
        );
      })}
      {bmi !== null && (
        <div
          role="status"
          className="mt-25 pt-25 border-t border-line-subtle type-body-md"
        >
          Calculated BMI: {bmi.toFixed(1)}
          {bmi < 20 && (
            <p className="type-body-sm text-ink-secondary mt-8">
              Your clinician will review this measurement before prescribing.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function IntakeFile({
  question,
  value,
  onUploaded,
  onUploadStart,
  onUploadEnd,
}: {
  question: IntakeQuestion;
  value: IntakeAnswer | undefined;
  onUploaded: (reference: IntakeAnswer) => void;
  onUploadStart?: () => void;
  onUploadEnd?: () => void;
}) {
  const upload = useUploadDocument();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  const references = Array.isArray(value)
    ? value
    : typeof value === "string" && value
      ? [value]
      : [];
  const limit = question.multiple ? Math.min(question.max_files ?? 10, 10) : 1;
  async function uploadFiles(files: File[]) {
    if (!files.length || uploading) return;
    if (question.multiple && references.length + files.length > limit) {
      setError(
        `Attach no more than ${limit} documents. Remove an attachment to add another.`,
      );
      return;
    }
    setError(null);
    setUploading(true);
    onUploadStart?.();
    const attached = question.multiple ? [...references] : [];
    try {
      for (const file of files) {
        const result = await upload.mutateAsync({
          file,
          purpose:
            question.key === "sermorelin.lab_documents" ? "lab" : "other",
        });
        attached.push(result.file_upload_id);
        setNames((current) => ({
          ...current,
          [result.file_upload_id]: file.name,
        }));
        onUploaded(question.multiple ? [...attached] : result.file_upload_id);
      }
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The document could not be uploaded. Please retry.",
      );
    } finally {
      setUploading(false);
      onUploadEnd?.();
    }
  }
  return (
    <div className="flex flex-col gap-8 type-body-sm">
      <label className="border border-dashed border-line hover:border-ink p-15 flex items-center gap-10 cursor-pointer">
        <FileText size={20} aria-hidden />
        <span>Choose {question.multiple ? "documents" : "a photo or PDF"}</span>
        <input
          className="sr-only"
          aria-label={question.label}
          type="file"
          accept="image/*,application/pdf"
          multiple={question.multiple}
          disabled={uploading}
          onChange={(event) => {
            const files = Array.from(event.target.files ?? []);
            event.target.value = "";
            void uploadFiles(files);
          }}
        />
      </label>
      {uploading && <p role="status">Uploading…</p>}
      {references.map((reference, index) => (
        <div
          key={reference}
          className="flex items-center justify-between gap-12"
        >
          <span>{names[reference] ?? `Document ${index + 1}`} attached</span>
          <button
            type="button"
            disabled={uploading}
            className="min-h-44 px-12 underline"
            aria-label={`Remove document ${index + 1}`}
            onClick={() => {
              const remaining = references.filter((item) => item !== reference);
              onUploaded(question.multiple ? remaining : null);
              setError(null);
            }}
          >
            Remove
          </button>
        </div>
      ))}
      {question.multiple && (
        <p className="text-ink-muted">
          {references.length} of {limit} documents attached
        </p>
      )}
      {error && (
        <p role="alert" className="text-[#C81E1E]">
          {error}
        </p>
      )}
    </div>
  );
}
